import {useBlocker} from 'react-router-dom';
import {useContext, useEffect, useRef} from 'react';
import {MessageDisplayerUtility} from "../MessageDisplayerUtility";
import {LeaveWithoutSavingWarningUtility} from "../LeaveWithoutSavingWarningUtility";
import {GlobalContext} from "../../admin-frame/AdminFrame";
import {useDebouncedFunction} from "./useDebouncedFunction";
import {useRenderableDataStore} from "./useRenderableDataStore";
import _ from "lodash";
import {GenericModel} from "./GenericModel";
import {SelectOption} from "../CommonUIElementTypes";

interface ModelFormEngineState {
    id?: number | string;
    invalidFields: any;
    validationErrors: string[];
    saved: boolean;
    supportingData?: any;
    validating: boolean;
}

// Engine for a form component for a data model. Tracks state of form and exposes form event handler functions (for field updates, save operations). Can fetch supporting data for the form on mount.
export function useModelFormEngine<ArbitraryData>(
    initialFormState: ArbitraryData,
    initialModel: GenericModel | undefined,
    fetchSupportingData: Function | undefined,
    validateForm: Function,
    formStateToSaveData: Function | undefined,
    save: Function,
    messageAdditionalConfig?: any
) {
    const context = useContext(GlobalContext);
    const initialState: ModelFormEngineState & ArbitraryData = {
        id: initialModel ? initialModel.id : undefined,
        invalidFields: {},
        validationErrors: [],
        saved: true,
        supportingData: null,
        validating: false,
        ...initialFormState,
    }
    const formDataStore =
        useRenderableDataStore<ModelFormEngineState & ArbitraryData>(initialState);

    let navigationBlocker = useBlocker(() => {
        return !formDataStore.data().saved
    })

    const disableNavigationBlocker = () => {
        LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings();
        formDataStore.updateAndRender({saved: true})
    }

    const onClickSave = useDebouncedFunction(() => {
        formDataStore.updateAndRender({loading: true})
        validateForm(handleValidationResult)
            .then(function (validationPasses: boolean) {
                if (validationPasses) {
                    let saveData = _.clone(formDataStore.data());
                    if (formStateToSaveData) {
                        saveData = formStateToSaveData(formDataStore.data())
                    }
                    save(saveData, disableNavigationBlocker);
                }
            })
            .catch(function (error: object) {
                console.log("Error validating form: ", error);
                MessageDisplayerUtility.error("An error occurred while saving.", messageAdditionalConfig)
            });
    });

    // tracks whether the component is still mounted, so supporting-data fetch
    // responses that arrive after unmount can be discarded rather than written
    // into the store
    const isMountedRef = useRef(true);

    // on mount, load supporting data if there is any that needs to be loaded
    useEffect(function () {
        isMountedRef.current = true;
        refreshSupportingData()
        return function () {
            isMountedRef.current = false;
        };
    }, []);

    function refreshSupportingData(additionalStateChange?: object) {
        if (fetchSupportingData) {
            formDataStore.updateAndRender({loading: true})
            fetchSupportingData()
                .then(function (supportingDataState: object) {
                    if (!isMountedRef.current) {
                        return;
                    }
                    let stateChange = {
                        ...supportingDataState,
                        loading: false
                    }
                    if (additionalStateChange) {
                        stateChange = {
                            ...additionalStateChange,
                            ...stateChange
                        }
                    }
                    formDataStore.updateAndRender(stateChange)
                })
                .catch(function (error: object) {
                    formDataStore.updateAndRender({loading: false})
                    console.log("Error loading form data: ", error);
                    MessageDisplayerUtility.error("An error occurred while loading form data.", messageAdditionalConfig)
                });
        } else {
            formDataStore.updateAndRender({loading: false})
        }
    }

    function updateAndRenderUnsavedData(dataUpdate: object) {
        LeaveWithoutSavingWarningUtility.enableLeaveWithoutSavingWarnings();
        formDataStore.updateAndRender({
            ...dataUpdate,
            saved: false
        })
    }

    function handleValidationResult(validationErrors: object[], invalidFields: string[], resolve?: Function) {
        if (validationErrors.length > 0) {
            let invalidFieldsUpdate: any = {};
            for (let y = 0; y < invalidFields.length; y++) {
                invalidFieldsUpdate[`${invalidFields[y]}_invalid`] = true;
            }
            formDataStore.updateAndRender({
                loading: false,
                invalidFields: invalidFieldsUpdate,
                validationErrors: validationErrors
            })
            // display validation errors
            for (let e = 0; e < validationErrors.length; e++) {
                MessageDisplayerUtility.error('' + validationErrors[e], messageAdditionalConfig);
            }
            if (resolve) {
                resolve(false);
            }
        } else {
            formDataStore.updateAndRender({
                loading: false,
                invalidFields: {},
                validationErrors: []
            })
            if (resolve) {
                resolve(true);
            }
        }
    }

    function getFormFieldClasses(originalClasses: string, fieldName: string | string[]) {
        let returnClasses = originalClasses;
        if (Array.isArray(fieldName)) {
            let allValid = true;
            for (let f = 0; f < fieldName.length; f++) {
                if (formDataStore.data().invalidFields[`${fieldName[f]}_invalid`]) {
                    allValid = false;
                    break;
                }
            }
            if (!allValid) {
                returnClasses += ' invalid';
            }
        } else {
            if (formDataStore.data().invalidFields[`${fieldName}_invalid`]) {
                returnClasses += ' invalid';
            }
        }
        return returnClasses;
    }

    function handleTextFieldChange(
        fieldName: string,
        event: { target: { value: string } }
    ) {
        if (event && event.target) {
            updateAndRenderUnsavedData({[fieldName]: event.target.value})
        }
    }

    function handleRadioOptionChange(fieldName: string, selectedOption: SelectOption) {
        if (selectedOption) {
            updateAndRenderUnsavedData({[fieldName]: selectedOption})
        }
    }

    // todo remove and replace with selectutility approach in all forms
    function handleSelectChange(fieldName: string, selectedOption: SelectOption) {
        if (selectedOption) {
            updateAndRenderUnsavedData({[fieldName]: selectedOption})
        }
    }

    function handleCheckboxChange(fieldName: string, value: boolean) {
        updateAndRenderUnsavedData({[fieldName]: value})
    }

    function forceValueToInteger(fieldName: string) {
        let fieldValue: string | number = (formDataStore.data() as any)[fieldName];
        if (fieldValue !== (parseInt(fieldValue as string) || 0)) {
            updateAndRenderUnsavedData({[fieldName]: parseInt(fieldValue as string) || 0})
        }
    }

    function forceValueToFloat(fieldName: string) {
        let fieldValue: string | number = (formDataStore.data() as any)[fieldName];
        if (fieldValue !== parseFloat(fieldValue as string) || 0) {
            updateAndRenderUnsavedData({[fieldName]: parseFloat(fieldValue as string) || 0})
        }
    }

    return {
        dataStore: formDataStore,
        onClickSave: onClickSave,
        getFormFieldClasses: getFormFieldClasses,
        handleTextFieldChange: handleTextFieldChange,
        handleRadioOptionChange: handleRadioOptionChange,
        handleCheckboxChange: handleCheckboxChange,
        handleSelectChange: handleSelectChange,
        forceValueToInteger: forceValueToInteger,
        forceValueToFloat: forceValueToFloat,
        updateAndRenderUnsavedData: updateAndRenderUnsavedData,
        refreshSupportingData: refreshSupportingData,
        navigationBlocker: navigationBlocker,
        disableNavigationBlocker: disableNavigationBlocker,
        handleValidationResult: handleValidationResult
    };
}
